import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FieldsRow,
  FieldsSection,
  FormCheckbox,
  FormTextField,
} from '../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { useLoaderData } from 'react-router-dom';
import { emailTabState } from '../../../states/email.state';
import { TCompanyConfigs } from '../../../../../../shared/services/companyConfig/companyConfig.service';

export const EmailTab: FC = () => {
  const defaultValues = useLoaderData() as Partial<TCompanyConfigs>;
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues,
  });
  const { t } = useTranslation();

  const {
    formState: { errors, dirtyFields },
    getValues,
    resetField,
  } = formMethods;

  useEffect(() => {
    const formStateSub = formMethods.watch((data, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldName = reason.name as keyof TCompanyConfigs;
        const dataToSave = { [fieldName]: data[fieldName] };
        emailTabState.pub.recordData(dataToSave);
      }
    });

    const emailTabStateSub = emailTabState.sub.state().subscribe(({ dataToSave }) => {
      for (const valuesKey in dataToSave) {
        if (valuesKey && dataToSave) {
          resetField(valuesKey as keyof TCompanyConfigs, {
            defaultValue: dataToSave[valuesKey as keyof typeof dataToSave],
            keepDirty: false,
          });
        }
      }
    });

    return () => {
      formStateSub.unsubscribe();
      emailTabStateSub.unsubscribe();
    };
  }, []);

  const onSubmit = (e: React.FocusEvent<HTMLFormElement>) => {
    const fieldName = e.target.name as keyof TCompanyConfigs;
    if (!errors[fieldName] && !!dirtyFields[fieldName]) {
      emailTabState.pub.saveData();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form noValidate onBlur={onSubmit}>
        <Stack
          direction='row'
          useFlexGap
          spacing={5}
          sx={{
            flexWrap: 'wrap',
          }}
        >
          <FieldsSection
            title={t('invoice.invoice')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForInvoice'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextField
                name='customEmailTextForInvoice'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForInvoice')}
                minRows={4}
                width='100%'
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('debitor.debitor')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForDebtor'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextField
                name='customEmailTextForDebtor'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForDebtor')}
                minRows={4}
                width='100%'
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('order.order')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForOrder'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextField
                name='customEmailTextForOrder'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForOrder')}
                minRows={4}
                width='100%'
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('offer.offer')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForOffer'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextField
                name='customEmailTextForOffer'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForOffer')}
                minRows={4}
                width='100%'
              />
            </FieldsRow>
          </FieldsSection>
        </Stack>
      </form>
    </FormProvider>
  );
};
